<template>
  <section class="band" id="marketplace">
    <div class="wrap">
      <div class="split" style="align-items:end">
        <div class="head">
          <h2 class="h2">Sell your airtime</h2>
        </div>
        <p class="lede">Set your rates. Advertisers book, upload and pay online.</p>
      </div>
      <div class="mp mt">
        <div>
          <div class="mapbox">
            <!-- The id is kept because the page's CSS sizes the map via #map. -->
            <div id="map" ref="map" role="img" aria-label="Map of Adverpost screens across South Australia"></div>
            <div class="map-legend"><span><i class="lg-pin"></i>Managed screen</span><span><i class="lg-pin pub"></i>Open for booking</span></div>
          </div>
          <p class="map-note">Zoom out to view advertising billboards outside of your city.</p>
        </div>
        <div class="rev">
          <div class="card">
            <span class="eyebrow">Marketplace revenue</span>
            <div class="big" style="margin-top:16px">$7,100</div>
            <div class="sub"><span class="up">▲ 18%</span> on September · 14 bookings</div>
            <div id="revBars" ref="revBars" class="bars" aria-hidden="true"></div>
          </div>
          <div class="card book" style="padding:10px 24px">
            <div><div><b>Barossa Wine Show</b><small>6 screens · Barossa</small></div><span>$1,240</span></div>
            <div><div><b>Hills Physio</b><small>2 screens · Adelaide Hills</small></div><span>$480</span></div>
            <div><div><b>Fleurieu Harvest Market</b><small>4 screens · Fleurieu</small></div><span>$760</span></div>
          </div>
        </div>
      </div>
      <div class="adv">
        <p><b>Advertiser?</b> Find a screen near your customers.</p>
        <a href="#/scheduling/map" @click.prevent="$emit('route', '/scheduling/map')">Find screens to book →</a>
      </div>
    </div>
  </section>
</template>

<script>
// Extracted verbatim from public/landing/index.html; in-page anchors emit
// instead of navigating, because the app uses a hash router.
import { drawRevenueBars } from "./widgets";
import { loadLeaflet, buildMap, showScreens, showManagedScreens, setMapTheme } from "./marketplaceMap";
import { RESOLVED_ATTR, isLandingDark } from "./theme";

// The same two calls as the old home-page map (StaticMap.vue), made once for
// the whole world instead of per visible area. The app's axios instance adds
// the Application-Token header.
const WORLD = "southlng=-180&northlng=180&southlat=-85&northlat=85&limit=all";
const BOOKABLE_SCREENS = `v1/displays?${WORLD}&public=true`;
const MANAGED_SCREENS = `v1/displays/private?${WORLD}`;

// Start building the map this far before it scrolls into view.
const MAP_LOOKAHEAD = "600px 0px";

export default {
  name: "MarketplaceSection",
  data() {
    return { map: null };
  },
  mounted() {
    drawRevenueBars(this.$refs.revBars);
    // Leaflet, the tiles and the two screen lists are only needed once the map
    // is close to the screen, so visitors who never scroll this far skip them.
    if ("IntersectionObserver" in window) {
      this.mapObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          this.mapObserver.disconnect();
          this.mapObserver = null;
          this.initMap();
        },
        { rootMargin: MAP_LOOKAHEAD }
      );
      this.mapObserver.observe(this.$refs.map);
    } else {
      this.initMap();
    }
  },
  beforeDestroy() {
    if (this.mapObserver) this.mapObserver.disconnect();
    if (this.themeObserver) this.themeObserver.disconnect();
    if (this.map) {
      if (this.map.__teardown) this.map.__teardown();
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    // The screen lists do not depend on Leaflet, so they load alongside it.
    // Real bookable screens replace the sample bookable pins (if that call
    // fails or returns nothing, the sample pins stay, as in the published
    // page); real managed screens become grey pins (if that call fails or
    // returns nothing, there are none). The API answers 404 for an empty list,
    // which lands in the same fallback.
    async initMap() {
      const rows = (url) =>
        this.$axios.get(url).then(
          (res) => (res && res.data && res.data.data) || [],
          () => []
        );
      const screens = Promise.all([rows(BOOKABLE_SCREENS), rows(MANAGED_SCREENS)]);
      try {
        const L = await loadLeaflet();
        if (this._isDestroyed || !this.$refs.map || !L) return;
        const built = buildMap(this.$refs.map, L, {
          dark: isLandingDark(),
          // A ~400 KB module, only fetched if the street tiles fail or are slow.
          loadCoastlines: () => import(/* webpackChunkName: "landing-coastlines" */ "@/assets/landing/coastlines"),
        });
        this.map = built.map;
        this.followTheme(built);
        const [bookable, managed] = await screens;
        if (this._isDestroyed || this.map !== built.map) return;
        showScreens(built, L, bookable);
        showManagedScreens(built, L, managed, bookable.map((d) => d.displayId));
      } catch (e) {
        // The map is decorative; a CDN failure must not break the page.
      }
    },
    // ThemeToggle marks the page's theme on <html>; it changes when the system
    // theme does, so the street map follows it.
    followTheme(built) {
      this.themeObserver = new MutationObserver(() => setMapTheme(built, isLandingDark()));
      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: [RESOLVED_ATTR],
      });
    },
  },
};
</script>
